import { useState } from 'react';
import { Users, Plus, Pencil, Trash2, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Input, Field } from '@/components/ui/Input';
import { SidePanel } from '@/components/ui/SidePanel';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { confirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { useIsStaff } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { api, ApiError, type Person, type WorkspaceSummary } from '@/lib/api';
import { useCompanyCtx } from './CompanyLayout';

/** The people register — stakeholders and interviewees around this client.
 * Everyone in the company can see it; only staff manage it. */
export function CompanyPeopleTab() {
  const { company } = useCompanyCtx();
  const isStaff = useIsStaff();
  const people = useAsync(() => api.listPeople(company.id), [company.id]);
  // null = closed; { person: null } = add; { person } = edit.
  const [editor, setEditor] = useState<{ person: Person | null } | null>(null);

  async function remove(p: Person) {
    const ok = await confirm({
      title: `Delete ${p.name}?`,
      description: 'Removed from every workspace they were involved in. Past deliverables are unaffected.',
    });
    if (!ok) return;
    try {
      await api.deletePerson(p.id);
      toast.success('Person deleted');
      people.reload();
    } catch (err) {
      toast.danger('Could not delete', err instanceof ApiError ? err.message : undefined);
    }
  }

  return (
    <>
      {isStaff && (
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={() => setEditor({ person: null })}>
            <Plus className="h-4 w-4" /> Add person
          </Button>
        </div>
      )}

      {people.status === 'loading' && <SkeletonRows rows={3} />}
      {people.status === 'error' && <ErrorState error={people.error} onRetry={people.reload} />}
      {people.status === 'success' && people.data.length === 0 && (
        <EmptyState
          icon={Users}
          title="No people yet"
          description="Stakeholders and interviewees for this client. Agents read this list on every run."
        />
      )}
      {people.status === 'success' && people.data.length > 0 && (
        <ul className="divide-y divide-border">
          {people.data.map((p) => (
            <li key={p.id} className="flex items-start gap-3 py-3.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-muted text-sm font-semibold text-heading">
                {p.name.trim()[0]?.toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg">
                  {p.name}
                  {p.title && <span className="ml-2 font-normal text-fg-muted">{p.title}</span>}
                </p>
                {p.email && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-fg-subtle">
                    <Mail className="h-3 w-3" aria-hidden /> {p.email}
                  </p>
                )}
                {p.involvements.length > 0 && (
                  <p className="mt-1.5 flex flex-wrap gap-1.5">
                    {p.involvements.map((i) => (
                      <Badge key={i.workspaceId} tone="neutral">
                        {i.workspaceName}
                        {i.role ? ` · ${i.role}` : ''}
                      </Badge>
                    ))}
                  </p>
                )}
              </div>
              {isStaff && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <IconButton label="Edit person" onClick={() => setEditor({ person: p })}>
                    <Pencil className="h-4 w-4" />
                  </IconButton>
                  <IconButton label="Delete person" onClick={() => remove(p)}>
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editor && (
        <PersonEditorPanel
          companyId={company.id}
          workspaces={company.workspaces}
          person={editor.person}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            people.reload();
          }}
        />
      )}
    </>
  );
}

function PersonEditorPanel({
  companyId,
  workspaces,
  person,
  onClose,
  onSaved,
}: {
  companyId: string;
  workspaces: WorkspaceSummary[];
  person: Person | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(person?.name ?? '');
  const [title, setTitle] = useState(person?.title ?? '');
  const [email, setEmail] = useState(person?.email ?? '');
  const [notes, setNotes] = useState(person?.notes ?? '');
  // workspaceId → role text; presence in the map = involved.
  const [involved, setInvolved] = useState<Map<string, string>>(
    new Map((person?.involvements ?? []).map((i) => [i.workspaceId, i.role ?? ''])),
  );
  const [busy, setBusy] = useState(false);

  function toggle(workspaceId: string) {
    setInvolved((prev) => {
      const next = new Map(prev);
      if (next.has(workspaceId)) next.delete(workspaceId);
      else next.set(workspaceId, '');
      return next;
    });
  }
  function setRole(workspaceId: string, role: string) {
    setInvolved((prev) => new Map(prev).set(workspaceId, role));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const fields = {
      name: name.trim(),
      title: title.trim() || null,
      email: email.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      const saved = person ? await api.updatePerson(person.id, fields) : await api.createPerson(companyId, fields);
      await api.setPersonWorkspaces(
        saved.id,
        [...involved.entries()].map(([workspaceId, role]) => ({ workspaceId, role: role.trim() || null })),
      );
      toast.success(person ? 'Person saved' : 'Person added');
      onSaved();
    } catch (err) {
      toast.danger('Could not save', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel title={person ? `Edit ${person.name}` : 'Add person'} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Name" htmlFor="pp-name">
          <Input id="pp-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Title" htmlFor="pp-title" hint="e.g. VIP Operations Lead.">
            <Input id="pp-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Email" htmlFor="pp-email" hint="Optional.">
            <Input id="pp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
        <Field label="Notes" htmlFor="pp-notes" hint="Internal context for agents and colleagues.">
          <textarea
            id="pp-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>

        <div>
          <p className="mb-1.5 text-sm font-medium text-fg">Workspace involvements</p>
          {workspaces.length === 0 ? (
            <p className="text-sm text-fg-muted">This company has no workspaces yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {workspaces.map((w) => {
                const checked = involved.has(w.id);
                return (
                  <li key={w.id} className="flex items-center gap-3 px-3 py-2">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(w.id)}
                        className="h-4 w-4 rounded border-border-strong accent-[rgb(var(--primary))]"
                      />
                      <span className="truncate text-sm text-fg">{w.name}</span>
                    </label>
                    {checked && (
                      <Input
                        value={involved.get(w.id) ?? ''}
                        onChange={(e) => setRole(w.id, e.target.value)}
                        placeholder="role, e.g. interviewee"
                        className="h-8 w-44 text-xs"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <Button type="submit" disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </SidePanel>
  );
}
